import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../../lib/api'
import { EmergencyDisclaimer } from '../../components/EmergencyDisclaimer'
import {
  CLINICAL_CONCERNS, THERAPY_MODALITIES, GENDER_PREFERENCES, SELF_PAY_BUDGET_RANGES,
  WEEKDAYS, TIME_WINDOWS, INSURANCE_TYPES, INSURANCE_CARRIERS, AGE_GROUPS,
} from '../../lib/vocab'

type Step = 'needs' | 'preferences' | 'location' | 'availability' | 'insurance' | 'review'

// Stored so the matches page can re-run/expand/update without a dedicated
// "latest intake" backend endpoint — this app only ever has one signed-in
// patient per browser session, so a local key is sufficient.
const LAST_INTAKE_KEY = 'atuune_last_intake_id'

export default function PatientIntakeWizard() {
  const navigate = useNavigate()
  const [step, setStep] = useState<Step>('needs')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const stepHeadingRef = useRef<HTMLDivElement>(null)

  // Moves keyboard/screen-reader focus to the step indicator whenever the
  // step changes, so navigating forward/back doesn't strand focus on a
  // button that no longer exists in the new step — aria-live announces the
  // change even for a user who hasn't moved focus there themselves.
  useEffect(() => {
    stepHeadingRef.current?.focus()
  }, [step])

  const [ageGroup, setAgeGroup] = useState('')
  const [concerns, setConcerns] = useState<string[]>([])
  const [modalities, setModalities] = useState<string[]>([])
  const [genderPreference, setGenderPreference] = useState('')
  const [carePreference, setCarePreference] = useState('either')
  const [culture, setCulture] = useState('')
  const [language, setLanguage] = useState('')

  const [country, setCountry] = useState('')
  const [stateProvince, setStateProvince] = useState('')
  const [city, setCity] = useState('')
  const [postalCode, setPostalCode] = useState('')
  const [maxTravelDistanceKm, setMaxTravelDistanceKm] = useState('')

  const [windows, setWindows] = useState<string[]>([])
  const [preferredWeekdays, setPreferredWeekdays] = useState<string[]>([])

  const [insuranceType, setInsuranceType] = useState('self_pay')
  const [carrier, setCarrier] = useState('')
  const [selfPayBudget, setSelfPayBudget] = useState('')

  function toggle(list: string[], setList: (v: string[]) => void, value: string) {
    setList(list.includes(value) ? list.filter((v) => v !== value) : [...list, value])
  }

  function next() {
    setError(null)
    if (step === 'needs') {
      if (!ageGroup) {
        setError('Please tell us who is seeking care before continuing.')
        return
      }
      setStep('preferences')
    }
    else if (step === 'preferences') setStep('location')
    else if (step === 'location') setStep('availability')
    else if (step === 'availability') setStep('insurance')
    else if (step === 'insurance') setStep('review')
  }

  function back() {
    setError(null)
    if (step === 'preferences') setStep('needs')
    else if (step === 'location') setStep('preferences')
    else if (step === 'availability') setStep('location')
    else if (step === 'insurance') setStep('availability')
    else if (step === 'review') setStep('insurance')
  }

  async function submit() {
    setBusy(true)
    setError(null)
    try {
      // Structured location lives on the Patient profile (not the intake
      // record) and triggers server-side geocoding — see PATCH /patients/me.
      if (country || stateProvince || city || postalCode) {
        await api('/patients/me', {
          method: 'PATCH',
          body: JSON.stringify({ country, state_province: stateProvince, city, postal_code: postalCode }),
        })
      }

      const intake = await api('/patients/me/intake', {
        method: 'POST',
        body: JSON.stringify({
          presenting_concerns: concerns,
          // modality_preference stays a single value for the existing rc_engine
          // scoring (unchanged this phase); the full multi-select is also
          // captured in preferences.therapy_modality for the Phase 2 engine.
          modality_preference: modalities[0] || null,
          language_preference: language || null,
          logistics: {
            state: stateProvince || null,
            format: carePreference,
            max_preferred_travel_distance_km: maxTravelDistanceKm ? Number(maxTravelDistanceKm) : null,
          },
          availability: { windows, preferred_weekdays: preferredWeekdays },
          preferences: {
            age_group: ageGroup || null,
            cultural_preference: culture || null,
            gender_preference: genderPreference || null,
            therapy_modality: modalities,
          },
          budget_or_insurance_context: {
            payment_method: insuranceType === 'self_pay' ? 'self_pay' : 'insurance',
            insurance_type: insuranceType,
            carrier: insuranceType !== 'self_pay' ? carrier || null : null,
            self_pay_budget: insuranceType === 'self_pay' ? selfPayBudget || null : null,
          },
        }),
      })
      localStorage.setItem(LAST_INTAKE_KEY, intake.id)
      await api('/patients/me/matches', { method: 'POST', body: JSON.stringify({ intake_id: intake.id }) })
      navigate('/patient/matches', { replace: true })
    } catch {
      setError('Could not submit your intake. Try again.')
    } finally {
      setBusy(false)
    }
  }

  const STEPS: Step[] = ['needs', 'preferences', 'location', 'availability', 'insurance', 'review']

  return (
    <div>
      <div className="page-head">
        <h1>Tell us about your care needs</h1>
        <div className="ph-sub" ref={stepHeadingRef} tabIndex={-1} aria-live="polite">Step {STEPS.indexOf(step) + 1} of {STEPS.length}</div>
      </div>
      <EmergencyDisclaimer />

      <div className="card" style={{ maxWidth: 560 }}>
        {step === 'needs' && (
          <div>
            <div className="field">
              <label id="who-label">Who is seeking care?</label>
              <div className="chip-list" role="group" aria-labelledby="who-label">
                {AGE_GROUPS.map((a) => (
                  <button type="button" key={a.value} className="chip" aria-pressed={ageGroup === a.value} style={{ background: ageGroup === a.value ? '#6B4EFF' : '#F1EEFB', color: ageGroup === a.value ? 'white' : '#6B4EFF' }} onClick={() => setAgeGroup(a.value)}>
                    {a.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="field">
              <label id="concerns-label">What brings you here today?</label>
              <div className="chip-list" role="group" aria-labelledby="concerns-label">
                {CLINICAL_CONCERNS.map((c) => (
                  <button type="button" key={c.value} className="chip" aria-pressed={concerns.includes(c.value)} style={{ background: concerns.includes(c.value) ? '#6B4EFF' : '#F1EEFB', color: concerns.includes(c.value) ? 'white' : '#6B4EFF' }} onClick={() => toggle(concerns, setConcerns, c.value)}>
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
            {error && <div className="form-error" role="alert">{error}</div>}
            <button className="btn full" onClick={next}>Continue</button>
          </div>
        )}

        {step === 'preferences' && (
          <div>
            <div className="field">
              <label id="modality-label">Therapy preference (optional)</label>
              <div className="chip-list" role="group" aria-labelledby="modality-label">
                {THERAPY_MODALITIES.map((m) => (
                  <button type="button" key={m.value} className="chip" aria-pressed={modalities.includes(m.value)} style={{ background: modalities.includes(m.value) ? '#6B4EFF' : '#F1EEFB', color: modalities.includes(m.value) ? 'white' : '#6B4EFF' }} onClick={() => toggle(modalities, setModalities, m.value)}>
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="field">
              <label>Therapist gender (optional)</label>
              <select className="inp" value={genderPreference} onChange={(e) => setGenderPreference(e.target.value)}>
                <option value="">Select…</option>
                {GENDER_PREFERENCES.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Care preference</label>
              <select className="inp" value={carePreference} onChange={(e) => setCarePreference(e.target.value)}>
                <option value="either">Either</option>
                <option value="virtual">Virtual</option>
                <option value="in_person">In person</option>
              </select>
            </div>
            <div className="form-grid">
              <div className="field"><label>Cultural preference (optional)</label><input className="inp" value={culture} onChange={(e) => setCulture(e.target.value)} /></div>
              <div className="field"><label>Language preference</label><input className="inp" value={language} onChange={(e) => setLanguage(e.target.value)} placeholder="English" /></div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn outline" onClick={back}>Back</button>
              <button className="btn full" onClick={next}>Continue</button>
            </div>
          </div>
        )}

        {step === 'location' && (
          <div>
            <div className="form-grid">
              <div className="field"><label>Country</label><input className="inp" value={country} onChange={(e) => setCountry(e.target.value)} /></div>
              <div className="field"><label>State / Province</label><input className="inp" value={stateProvince} onChange={(e) => setStateProvince(e.target.value)} placeholder="NY" /></div>
            </div>
            <div className="form-grid">
              <div className="field"><label>City</label><input className="inp" value={city} onChange={(e) => setCity(e.target.value)} /></div>
              <div className="field"><label>Postal / ZIP code</label><input className="inp" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} /></div>
            </div>
            <div className="field">
              <label>Maximum preferred travel distance in km (optional)</label>
              <input className="inp" type="number" min={0} value={maxTravelDistanceKm} onChange={(e) => setMaxTravelDistanceKm(e.target.value)} placeholder="We'll only go further if we need to, to find you a good fit" />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn outline" onClick={back}>Back</button>
              <button className="btn full" onClick={next}>Continue</button>
            </div>
          </div>
        )}

        {step === 'availability' && (
          <div>
            <div className="field">
              <label id="windows-label">Preferred time of day</label>
              <div className="chip-list" role="group" aria-labelledby="windows-label">
                {TIME_WINDOWS.map((w) => (
                  <button type="button" key={w.value} className="chip" aria-pressed={windows.includes(w.value)} style={{ background: windows.includes(w.value) ? '#6B4EFF' : '#F1EEFB', color: windows.includes(w.value) ? 'white' : '#6B4EFF' }} onClick={() => toggle(windows, setWindows, w.value)}>
                    {w.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="field">
              <label id="weekdays-label">Preferred weekdays</label>
              <div className="chip-list" role="group" aria-labelledby="weekdays-label">
                {WEEKDAYS.map((d) => (
                  <button type="button" key={d.value} className="chip" aria-pressed={preferredWeekdays.includes(d.value)} style={{ background: preferredWeekdays.includes(d.value) ? '#6B4EFF' : '#F1EEFB', color: preferredWeekdays.includes(d.value) ? 'white' : '#6B4EFF' }} onClick={() => toggle(preferredWeekdays, setPreferredWeekdays, d.value)}>
                    {d.label}
                  </button>
                ))}
              </div>
            </div>
            <p className="muted" style={{ fontSize: 12.5 }}>
              These are preferences, not a booking — you'll pick an actual appointment time from your
              therapist's real availability after you choose one.
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn outline" onClick={back}>Back</button>
              <button className="btn full" onClick={next}>Continue</button>
            </div>
          </div>
        )}

        {step === 'insurance' && (
          <div>
            <div className="field">
              <label>How will you pay?</label>
              <select className="inp" value={insuranceType} onChange={(e) => setInsuranceType(e.target.value)}>
                {INSURANCE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            {insuranceType !== 'self_pay' && (
              <div className="field">
                <label>Carrier (optional)</label>
                <select className="inp" value={carrier} onChange={(e) => setCarrier(e.target.value)}>
                  <option value="">Not sure / prefer not to say</option>
                  {INSURANCE_CARRIERS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
            )}
            {insuranceType === 'self_pay' && (
              <div className="field">
                <label>Budget (optional)</label>
                <select className="inp" value={selfPayBudget} onChange={(e) => setSelfPayBudget(e.target.value)}>
                  <option value="">Prefer not to say</option>
                  {SELF_PAY_BUDGET_RANGES.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
                </select>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn outline" onClick={back}>Back</button>
              <button className="btn full" onClick={next}>Continue</button>
            </div>
          </div>
        )}

        {step === 'review' && (
          <div>
            <div className="row-list" style={{ marginBottom: 14 }}>
              <div className="row"><span>Who is seeking care</span><span className="muted">{AGE_GROUPS.find((a) => a.value === ageGroup)?.label || '—'}</span></div>
              <div className="row"><span>What brings you here</span><span className="muted">{concerns.join(', ') || '—'}</span></div>
              <div className="row"><span>Therapy preference</span><span className="muted">{modalities.join(', ') || '—'}</span></div>
              <div className="row"><span>Therapist gender</span><span className="muted">{genderPreference || 'No preference'}</span></div>
              <div className="row"><span>Care preference</span><span className="muted">{carePreference}</span></div>
              <div className="row"><span>Language</span><span className="muted">{language || '—'}</span></div>
              <div className="row"><span>Location</span><span className="muted">{[city, stateProvince, country].filter(Boolean).join(', ') || '—'}</span></div>
              <div className="row"><span>Availability</span><span className="muted">{[...windows, ...preferredWeekdays].join(', ') || '—'}</span></div>
              <div className="row"><span>Payment</span><span className="muted">{insuranceType}{carrier ? ` (${carrier})` : ''}{selfPayBudget ? ` (${selfPayBudget})` : ''}</span></div>
            </div>
            {error && <div className="form-error" role="alert">{error}</div>}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn outline" onClick={back} disabled={busy}>Back</button>
              <button className="btn full" onClick={submit} disabled={busy}>{busy ? 'Searching…' : 'Submit & find matches'}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export { LAST_INTAKE_KEY }
